import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { ContentHeaderKey } from "src/app/constants/content-header";
import { TableColumnType } from "src/app/constants/table";
import { AgentCompany, AgentUser, CompanyAgentsSelected } from "src/app/models/agent";
import { Broker, WorkingBroker } from "src/app/models/broker";
import { TableColumn } from "src/app/models/table";

import { AgentService } from "src/app/services/agent.service";
import { FormValidationService } from "src/app/services/form-validation.service";
import { MaskService } from "src/app/services/mask.service";
import { SessionService } from "src/app/services/storage/session.service";

@Component({
    selector: 'mwp-manage-company-assign',
    templateUrl: './company-assign.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentCompanyAssignComponent implements OnInit {
    public brokers: Broker[];
    public companies: AgentCompany[];

    public agentControlForm: FormGroup;
    public agentForm: FormGroup;
    public agentFormArrayName: string = 'agentFormArray';
    public formSelectCheckbox: string = 'isSelected';
    public selectedCompanyId: number;
    public isAssign: boolean;

    public agentMatTableData: MatTableDataSource<AgentUser>;
    public agentColumns: TableColumn[];
    public apiUrl = ContentHeaderKey.CounselorCompanyAssign;
    public baseAgentColumns: TableColumn[] = [
        {
            columnName: 'Name',
            propertyId: 'name',
            columnType: TableColumnType.label
        },
        {
            columnName: 'Email',
            propertyId: 'email',
            columnType: TableColumnType.label
        },
        {
            columnName: 'Phone',
            propertyId: 'phone',
            columnType: TableColumnType.label
        },
    ];

    constructor(
        private agentService: AgentService,
        private formBuilder: FormBuilder,
        private sessionService: SessionService,
        private maskService: MaskService,
    ) {
    }

    ngOnInit(): void {
        this.agentControlForm = this.formBuilder.group({
            brokerId: [this.sessionService.session.selectedBrokerId, Validators.required],
            companyID: [null, Validators.required]
        });

        this.agentService.getBrokers().subscribe((response: Broker[]) => {
            this.brokers = response;
        });

        this.setBroker(this.sessionService.session.selectedBrokerId);
    }

    public brokerChange(event: number): void {
        this.setBroker(event);
    }

    public setBroker(id: number): void {
        let l_WorkingBroker = new WorkingBroker();
        l_WorkingBroker.Id = id;

        this.agentService.setWorkingBroker(l_WorkingBroker).subscribe(() => {
            this.agentControlForm.get('companyID').setValue(null);
            this.agentForm = null;
            this.agentMatTableData = null;
            this.companies = null;
            this.selectedCompanyId = null;

            this.agentService.getAgentCompaniesByBroker(l_WorkingBroker).subscribe((response: AgentCompany[]) => {
                this.companies = response;
            });
        });
    }

    public companyChange(event: number): void {
        this.selectedCompanyId = event;
        this.buildUnassignForm();
    }

    public buildUnassignForm(): void {
        const l_AgentCompanyPayload = new AgentCompany();
        l_AgentCompanyPayload.companyID = this.selectedCompanyId;

        this.agentService.getCompanyAssignedAgents(l_AgentCompanyPayload).subscribe((response: AgentUser[]) => {
            this.isAssign = false;
            this.buildAgentForm(response);
        });
    }

    public buildAssignForm(): void {
        const l_AgentCompanyPayload = new AgentCompany();
        l_AgentCompanyPayload.companyID = this.selectedCompanyId;
        this.agentService.getAgentsNotAssignedToCompany(l_AgentCompanyPayload).subscribe((response: AgentUser[]) => {
            this.isAssign = true;
            this.buildAgentForm(response);
        });
    }

    private buildAgentForm(agentUsers: AgentUser[]): void {
        if (agentUsers) {
            this.agentColumns = null;
            this.agentColumns = Object.assign([], this.baseAgentColumns);
            this.agentColumns.push({
                propertyId: this.formSelectCheckbox,
                columnName: this.isAssign ? 'Assign' : 'Remove',
                columnType: TableColumnType.checkBox,
                width: "10%"
            });

            this.agentMatTableData = new MatTableDataSource<AgentUser>();
            const l_Agents = new Array<AgentUser>();

            let l_FormArray: FormArray = new FormArray([], FormValidationService.formArrayRequiresCheckboxChecked(this.formSelectCheckbox));
            agentUsers.forEach((agent: AgentUser) => {
                agent.name = `${agent.firstName} ${agent.lastName}`;
                l_FormArray.push(
                    this.formBuilder.group({
                        name: [agent.name],
                        id: [agent.id],
                        email: [agent.email],
                        phone: [this.maskService.phoneFormatInbound(agent.phone)],
                        [this.formSelectCheckbox]: [false],
                    })
                );
            });

            this.agentMatTableData.data = agentUsers;

            this.agentForm = this.formBuilder.group({
                [this.agentFormArrayName]: l_FormArray,
            });
        } else {
            this.agentForm = null;
            this.agentMatTableData = null;
        }
    }

    public unassignAgents(): void {
        const l_CompanyAgentsSelected = this.selectedAgents();

        this.agentService.unAssignAgentsFromCompany(l_CompanyAgentsSelected).subscribe(() => {
            this.buildUnassignForm();
        });
    }

    // After assigning agents we need to build the form to unassign to display assigned agents to the company
    public assignAgents(): void {
        const l_CompanyAgentsSelected = this.selectedAgents();

        this.agentService.assignAgentsToCompany(l_CompanyAgentsSelected).subscribe(() => {
            this.buildUnassignForm();
        });
    }

    private selectedAgents(): CompanyAgentsSelected {
        const l_CompanyAgentsSelected = new CompanyAgentsSelected();
        let l_SelectedAgents = new Array<number>();

        const l_FormArray = this.agentForm.get(this.agentFormArrayName) as FormArray;
        l_FormArray.controls.map((cfa) => {
            if (cfa.get([this.formSelectCheckbox]).value)
                l_SelectedAgents.push(cfa.get(['id']).value);
        });

        l_CompanyAgentsSelected.companyID = this.agentControlForm.get('companyID').value;
        l_CompanyAgentsSelected.agentIDs = l_SelectedAgents;

        return l_CompanyAgentsSelected;
    }
}
